import React, { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { useSelection } from '../context/SelectionContext';
import { useTabletMode } from '../context/TabletModeContext';
import { productsApi } from '../lib/api';
import { 
  trackSelectionOpened,
  trackShowToWaiterClicked,
  trackRemoveFromSelection,
  trackQtyChanged,
  trackSuggestionTypeClicked,
  trackSuggestionAdd
} from '../lib/analytics';
import { 
  X, Minus, Plus, Trash2, Hand, Sparkles, 
  ShoppingBag, ArrowLeft, Check, Send, Loader2, ClipboardList, Bell, ChevronRight
} from 'lucide-react';
import { ScrollArea } from './ui/scroll-area';
import { Button } from './ui/button';
import { ThankYouReviewModal } from './ReviewPrompt';
import SorteoPopup from './SorteoPopup';
import { Badge } from './ui/badge';
import { CustomerOrdersPanel } from './CustomerOrdersPanel';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Placeholder images
const productPlaceholders = [
  'https://images.unsplash.com/photo-1504674900247-0877df9cc836?w=400&q=80',
  'https://images.unsplash.com/photo-1476224203421-9ac39bcb3327?w=400&q=80',
  'https://images.unsplash.com/photo-1473093295043-cdd812d0e601?w=400&q=80',
];

const translations = {
  es: {
    title: 'Mi selección',
    empty: 'Tu selección está vacía',
    emptySubtitle: 'Añade productos desde la carta para verlos aquí',
    disclaimer: 'Tu selección no es un pedido confirmado. Muéstrala al personal para finalizar.',
    disclaimerTable: 'Confirma tu pedido para enviarlo directamente a cocina',
    disclaimerWaiterOnly: 'Prepara tu lista y muéstrala al camarero cuando estés listo para pedir',
    total: 'Total estimado',
    showWaiter: 'Confirmar en barra',
    sendOrder: 'Enviar pedido',
    showToWaiter: 'Mostrar al camarero',
    suggestions: '¿Te apetece algo más?',
    clear: 'Vaciar selección',
    back: 'Volver',
    added: '¡Añadido!',
    close: 'Cerrar',
    yourSelection: 'Tu selección',
    localSection: 'Consumo en local',
    shopSection: 'Para llevar',
    subtotalLocal: 'Subtotal local',
    subtotalShop: 'Subtotal tienda',
    orderSent: '¡Pedido enviado!',
    orderSentMsg: 'Tu pedido ha sido enviado a cocina/barra. Te lo traeremos pronto.',
    orderError: 'Error al enviar el pedido',
    table: 'Mesa',
    viewOrders: 'Ver mis pedidos',
    addMore: 'Añadir más',
    notificationPrompt: '¿Quieres recibir alertas cuando tu pedido esté listo?',
    enableNotifications: 'Activar notificaciones',
    waiterModeTitle: 'Lista de pedido',
    waiterModeDescription: 'Muestra esta lista al camarero para confirmar tu pedido',
    shopBanner: '¿Conoces nuestra tienda?',
    shopBannerSubtitle: 'Productos artesanos para llevar a casa',
    visitShop: 'Ver tienda',
    suggestionCategories: {
      sweet: 'Algo dulce',
      savory: 'Algo salado',
      drink: 'Algo para beber',
      snack: 'Para picar'
    },
    backToCategories: 'Otras opciones'
  },
  en: {
    title: 'My selection',
    empty: 'Your selection is empty',
    emptySubtitle: 'Add products from the menu to see them here',
    disclaimer: 'Your selection is not a confirmed order. Show it to staff to finalize.',
    disclaimerTable: 'Confirm your order to send it directly to the kitchen',
    disclaimerWaiterOnly: 'Prepare your list and show it to the waiter when ready to order',
    total: 'Estimated total',
    showWaiter: 'Confirm at counter',
    sendOrder: 'Send order',
    showToWaiter: 'Show to waiter',
    suggestions: 'Would you like something else?',
    clear: 'Clear selection',
    back: 'Back',
    added: 'Added!',
    close: 'Close',
    yourSelection: 'Your selection',
    localSection: 'Dine-in',
    shopSection: 'Take away',
    subtotalLocal: 'Dine-in subtotal',
    subtotalShop: 'Shop subtotal',
    orderSent: 'Order sent!',
    orderSentMsg: 'Your order has been sent to the kitchen/bar. We\'ll bring it to you soon.',
    orderError: 'Error sending order',
    table: 'Table',
    viewOrders: 'View my orders',
    addMore: 'Add more',
    notificationPrompt: 'Want to receive alerts when your order is ready?',
    enableNotifications: 'Enable notifications',
    waiterModeTitle: 'Order list',
    waiterModeDescription: 'Show this list to your waiter to confirm your order',
    shopBanner: 'Have you seen our shop?',
    shopBannerSubtitle: 'Artisan products to take home',
    visitShop: 'Visit shop',
    suggestionCategories: {
      sweet: 'Something sweet',
      savory: 'Something savory',
      drink: 'Something to drink',
      snack: 'A snack'
    },
    backToCategories: 'Other options'
  },
  cat: {
    title: 'La meva selecció',
    empty: 'La teva selecció està buida',
    emptySubtitle: 'Afegeix productes de la carta per veure\'ls aquí',
    disclaimer: 'La teva selecció no és una comanda confirmada. Mostra-la al personal per finalitzar.',
    disclaimerTable: 'Confirma la comanda per enviar-la directament a cuina',
    disclaimerWaiterOnly: 'Prepara la teva llista i mostra-la al cambrer quan estiguis llest per demanar',
    total: 'Total estimat',
    showWaiter: 'Confirmar a barra',
    sendOrder: 'Enviar comanda',
    showToWaiter: 'Mostrar al cambrer',
    suggestions: 'Et ve de gust alguna cosa més?',
    clear: 'Buidar selecció',
    back: 'Tornar',
    added: 'Afegit!',
    close: 'Tancar',
    yourSelection: 'La teva selecció',
    localSection: 'Consum al local',
    shopSection: 'Per emportar',
    subtotalLocal: 'Subtotal local',
    subtotalShop: 'Subtotal botiga',
    orderSent: 'Comanda enviada!',
    orderSentMsg: 'La teva comanda ha estat enviada a cuina/barra. Te la portarem aviat.',
    orderError: 'Error en enviar la comanda',
    table: 'Taula',
    viewOrders: 'Veure les meves comandes',
    addMore: 'Afegir més',
    notificationPrompt: 'Vols rebre alertes quan la teva comanda estigui llesta?',
    enableNotifications: 'Activar notificacions',
    waiterModeTitle: 'Llista de comanda',
    waiterModeDescription: 'Mostra aquesta llista al cambrer per confirmar la teva comanda',
    shopBanner: 'Coneixes la nostra botiga?',
    shopBannerSubtitle: 'Productes artesans per emportar a casa',
    visitShop: 'Veure botiga',
    suggestionCategories: {
      sweet: 'Alguna cosa dolça',
      savory: 'Alguna cosa salada',
      drink: 'Alguna cosa per beure',
      snack: 'Per picar'
    },
    backToCategories: 'Altres opcions'
  },
  fr: {
    title: 'Ma sélection',
    empty: 'Votre sélection est vide',
    emptySubtitle: 'Ajoutez des produits depuis le menu pour les voir ici',
    disclaimer: 'Votre sélection n\'est pas une commande confirmée. Montrez-la au personnel pour finaliser.',
    disclaimerTable: 'Confirmez votre commande pour l\'envoyer directement en cuisine',
    disclaimerWaiterOnly: 'Préparez votre liste et montrez-la au serveur quand vous êtes prêt à commander',
    total: 'Total estimé',
    showWaiter: 'Confirmer au comptoir',
    sendOrder: 'Envoyer la commande',
    showToWaiter: 'Montrer au serveur',
    suggestions: 'Vous désirez autre chose ?',
    clear: 'Vider la sélection',
    back: 'Retour',
    added: 'Ajouté !',
    close: 'Fermer',
    yourSelection: 'Votre sélection',
    localSection: 'Sur place',
    shopSection: 'À emporter',
    subtotalLocal: 'Sous-total sur place',
    subtotalShop: 'Sous-total boutique',
    orderSent: 'Commande envoyée !',
    orderSentMsg: 'Votre commande a été envoyée en cuisine/bar. Nous vous l\'apportons bientôt.',
    orderError: 'Erreur lors de l\'envoi',
    table: 'Table',
    viewOrders: 'Voir mes commandes',
    addMore: 'Ajouter plus',
    notificationPrompt: 'Voulez-vous recevoir des alertes quand votre commande est prête ?',
    enableNotifications: 'Activer les notifications',
    waiterModeTitle: 'Liste de commande',
    waiterModeDescription: 'Montrez cette liste au serveur pour confirmer votre commande',
    shopBanner: 'Connaissez-vous notre boutique ?',
    shopBannerSubtitle: 'Produits artisanaux à emporter',
    visitShop: 'Voir boutique',
    suggestionCategories: {
      sweet: 'Quelque chose de sucré',
      savory: 'Quelque chose de salé',
      drink: 'Une boisson',
      snack: 'Un encas'
    },
    backToCategories: 'Autres options'
  }
};

export const SelectionSheet = () => {
  const { language, localize } = useLanguage();
  const { isTablet } = useTabletMode();
  const { 
    items, 
    isOpen, 
    setIsOpen, 
    updateQuantity, 
    removeItem, 
    clearSelection,
    getTotal,
    getLocalTotal,
    getShopTotal,
    getLocalItems,
    getShopItems,
    getCategoryIds
  } = useSelection();
  
  const [suggestions, setSuggestions] = useState([]);
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [showWaiterModal, setShowWaiterModal] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [showSorteo, setShowSorteo] = useState(false);
  const [sorteoActive, setSorteoActive] = useState(false); // Sorteo disabled

  // Sorteo disabled — keeping state for future re-activation
  // useEffect(() => {
  //   fetch(`${API_URL}/api/turbo-marketing/sorteo/current`)
  //     .then(r => r.json())
  //     .then(d => setSorteoActive(d.is_active || false))
  //     .catch(() => setSorteoActive(false));
  // }, []);
  const [selectedSuggestionType, setSelectedSuggestionType] = useState(null);
  const [categorySuggestions, setCategorySuggestions] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [tableNumber, setTableNumber] = useState(null);
  const [sendingOrder, setSendingOrder] = useState(false);
  const [orderSent, setOrderSent] = useState(false);
  const [orderError, setOrderError] = useState(null);
  const [showOrdersPanel, setShowOrdersPanel] = useState(false);
  const [orderMode, setOrderMode] = useState('direct_send'); // 'direct_send' or 'waiter_only'
  const t = translations[language] || translations.es;

  // Fetch order mode setting
  useEffect(() => {
    const fetchOrderMode = async () => {
      try {
        const response = await fetch(`${API_URL}/api/admin/order-mode`);
        if (response.ok) {
          const data = await response.json();
          setOrderMode(data.order_mode || 'direct_send');
        }
      } catch (error) {
        console.error('Error fetching order mode:', error);
      }
    };
    fetchOrderMode();
  }, []);

  // Check for table number from sessionStorage
  useEffect(() => {
    const mesa = sessionStorage.getItem('mesa_numero');
    if (mesa) {
      setTableNumber(parseInt(mesa));
    }
  }, [isOpen]);

  // Category mappings for suggestions
  const suggestionTypeCategories = {
    sweet: ['cat-dulces', 'cat-reposteria'],
    savory: ['cat-picar', 'cat-bocadillos-tradicionales', 'cat-bocadillos-especiales', 'cat-pizzas', 'cat-ensaladas'],
    drink: ['cat-cervezas', 'cat-bodega', 'cat-licores', 'cat-cocteles', 'cat-refrescos', 'cat-cafe-plantas'],
    snack: ['cat-picar']
  };

  useEffect(() => {
    if (isOpen && items.length > 0) {
      loadAllProducts();
    }
  }, [isOpen, items.length]);

  const loadAllProducts = async () => {
    try {
      const response = await productsApi.getAll({ active_only: true });
      setAllProducts(response.data);
    } catch (error) {
      console.error('Error loading products:', error);
    }
  };

  const handleSelectSuggestionType = (type) => {
    trackSuggestionTypeClicked(type);
    setSelectedSuggestionType(type);
    
    const categoryIds = suggestionTypeCategories[type] || [];
    const selectedIds = items.map(i => i.id);
    
    const filtered = allProducts.filter(
      p => categoryIds.includes(p.category_id) && !selectedIds.includes(p.id)
    );
    
    // Shuffle and take up to 4
    const shuffled = filtered.sort(() => 0.5 - Math.random());
    setCategorySuggestions(shuffled.slice(0, 4));
  };

  const handleBackToCategories = () => {
    setSelectedSuggestionType(null);
    setCategorySuggestions([]);
  };


  const handleShowWaiter = () => {
    trackShowToWaiterClicked(items.length, getTotal());
    setShowWaiterModal(true);
  };

  // Send order to backend (when using table QR)
  const handleSendOrder = async () => {
    if (!tableNumber) return;
    
    setSendingOrder(true);
    setOrderError(null);
    
    try {
      const sessionId = localStorage.getItem('pont10_session_id');
      const orderItems = items.map(item => ({
        product_id: item.id,
        product_name: localize(item.name),
        quantity: item.quantity,
        price: item.price + (item.selectedOptions?.reduce((acc, opt) => acc + (opt.price_modifier || 0), 0) || 0),
        options: item.selectedOptions?.map(opt => localize(opt.name)) || [],
        notes: item.notes || ''
      }));

      const response = await fetch(`${API_URL}/api/pedidos`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          mesa_numero: tableNumber,
          items: orderItems,
          notes: '',
          language: language,
          client_session_id: sessionId
        })
      });

      if (response.ok) {
        setOrderSent(true);
        clearSelection();
        setTimeout(() => {
          setOrderSent(false);
          setShowOrdersPanel(true);
          // Show sorteo or review after order sent
          setTimeout(() => {
            if (sorteoActive) {
              setShowSorteo(true);
            } else {
              setShowReviewModal(true);
            }
          }, 500);
        }, 2000);
      } else {
        const error = await response.json();
        setOrderError(error.detail || t.orderError);
      }
    } catch (error) {
      console.error('Error sending order:', error);
      setOrderError(t.orderError);
    } finally {
      setSendingOrder(false);
    }
  };

  const handleRemoveItem = (item) => {
    trackRemoveFromSelection(item.id, item.name?.es || item.name);
    removeItem(item.id, item.selectedOptions);
  };

  const handleQtyChange = (item, newQty) => {
    trackQtyChanged(item.id, item.quantity, newQty);
    updateQuantity(item.id, item.selectedOptions, newQty);
  };

  const getItemImage = (item, index) => {
    if (item.image) return item.image;
    return productPlaceholders[index % productPlaceholders.length];
  };

  const getItemTotal = (item) => {
    const optionsExtra = item.selectedOptions?.reduce(
      (acc, opt) => acc + (opt.price_modifier || 0), 0
    ) || 0;
    return (item.price + optionsExtra) * item.quantity;
  };

  if (!isOpen) return null;

  return (
    <>
      <div 
        className="fixed inset-0 z-50 flex flex-col overflow-hidden"
        style={{
          backgroundColor: 'var(--sand-50)',
          // Clear the fixed TopCartBar (taller on tablet) + iOS safe-area.
          paddingTop: `calc(env(safe-area-inset-top, 0px) + ${isTablet ? '90px' : '64px'})`,
        }}
        data-testid="selection-sheet"
      >
        {/* Header */}
        <header 
          className="sticky top-0 z-10 px-4 py-4 flex items-center justify-between shrink-0"
          style={{ 
            backgroundColor: 'var(--sand-50)',
            borderBottom: '1px solid rgba(45, 42, 38, 0.08)' 
          }}
        >
          <button
            onClick={() => setIsOpen(false)}
            className="back-btn"
            data-testid="close-selection-btn"
            style={isTablet ? { fontSize: '18px', padding: '12px 20px', gap: '10px' } : undefined}
          >
            <ArrowLeft size={isTablet ? 26 : 18} />
            {t.back}
          </button>
          <div className="flex flex-col items-center">
            <h1 
              className={`font-serif font-medium ${isTablet ? 'text-3xl' : 'text-2xl'}`}
              style={{ color: 'var(--stone-800)' }}
            >
              {t.title}
            </h1>
          </div>
          {/* Spacer to balance header */}
          <div className={isTablet ? 'w-24' : 'w-16'} />
        </header>

        {/* Table indicator - discrete, only shows table number */}
        {tableNumber && (
          <div 
            className="px-4 py-2 flex items-center justify-center"
            style={{ backgroundColor: '#F5F5F5', borderBottom: '1px solid #E0E0E0' }}
          >
            <span className="text-gray-500 text-sm">
              {language === 'es' ? 'Mesa' : language === 'en' ? 'Table' : language === 'fr' ? 'Table' : 'Taula'} 
              <span className="font-semibold text-gray-700 ml-1">#{tableNumber}</span>
            </span>
          </div>
        )}

        {/* Order sent success message */}
        {orderSent && (
          <div className="fixed inset-0 z-[80] flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(26, 24, 22, 0.85)' }}>
            <div className="bg-white rounded-2xl p-8 text-center max-w-sm animate-scale-in">
              <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: 'rgba(183,140,60,0.15)' }}>
                <Check size={40} style={{ color: '#B78C3C' }} />
              </div>
              <h2 className="font-serif text-2xl mb-2" style={{ color: 'var(--stone-800)' }}>{t.orderSent}</h2>
              <p className="text-gray-600 mb-4">{t.orderSentMsg}</p>
              {tableNumber && (
                <div className="mb-4 p-3 rounded-lg" style={{ backgroundColor: 'rgba(183,140,60,0.1)' }}>
                  <span className="font-semibold" style={{ color: '#B78C3C' }}>{t.table} #{tableNumber}</span>
                </div>
              )}
              
              {/* Notification prompt */}
              {'Notification' in window && Notification.permission !== 'granted' && Notification.permission !== 'denied' && (
                <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <Bell size={18} className="text-amber-600" />
                    <span className="text-sm font-medium text-amber-800">{t.notificationPrompt}</span>
                  </div>
                  <Button
                    onClick={async () => {
                      if ('Notification' in window) {
                        const permission = await Notification.requestPermission();
                        if (permission === 'granted') {
                          // Show a test notification
                          new Notification('¡Notificaciones activadas!', {
                            body: 'Te avisaremos cuando tu pedido esté listo',
                            icon: '/favicon.ico'
                          });
                        }
                      }
                    }}
                    size="sm"
                    className="w-full bg-amber-500 hover:bg-amber-600 text-white"
                  >
                    {t.enableNotifications}
                  </Button>
                </div>
              )}
              
              {/* iOS Safari warning */}
              {typeof navigator !== 'undefined' && /iPhone|iPad|iPod/.test(navigator.userAgent) && !('standalone' in navigator && navigator.standalone) && (
                <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800">
                  <p className="font-medium mb-1">📱 Consejo para iPhone:</p>
                  <p>Para recibir notificaciones, añade esta página a tu pantalla de inicio (Compartir → Añadir a pantalla de inicio)</p>
                </div>
              )}

              {/* Action buttons */}
              <div className="flex gap-3">
                <Button
                  onClick={() => {
                    setOrderSent(false);
                    setShowOrdersPanel(true);
                  }}
                  variant="outline"
                  className="flex-1"
                >
                  <ClipboardList size={18} className="mr-2" />
                  {t.viewOrders}
                </Button>
                <Button
                  onClick={() => {
                    setOrderSent(false);
                    setIsOpen(false);
                  }}
                  className="flex-1 bg-[#B78C3C] hover:bg-[#9E7530]"
                >
                  <Plus size={18} className="mr-2" />
                  {t.addMore}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Customer Orders Panel */}
        <CustomerOrdersPanel
          mesaNumero={tableNumber}
          isOpen={showOrdersPanel}
          onClose={() => setShowOrdersPanel(false)}
          language={language}
        />

        {/* Content */}
        <ScrollArea className="flex-1">
          <div className="px-4 py-4 max-w-lg mx-auto">
            {items.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <div 
                  className="w-20 h-20 rounded-full flex items-center justify-center mb-6"
                  style={{ backgroundColor: 'var(--sand-200)' }}
                >
                  <ShoppingBag size={32} style={{ color: 'var(--stone-500)' }} />
                </div>
                <h2 className="font-serif text-2xl mb-2" style={{ color: 'var(--stone-800)' }}>
                  {t.empty}
                </h2>
                <p style={{ color: 'var(--stone-500)' }} className="mb-6">
                  {t.emptySubtitle}
                </p>
                
                {/* Show "View my orders" button when at a table */}
                {tableNumber && (
                  <Button
                    onClick={() => setShowOrdersPanel(true)}
                    variant="outline"
                    className="flex items-center gap-2"
                  >
                    <ClipboardList size={18} />
                    {t.viewOrders}
                  </Button>
                )}
              </div>
            ) : (
              <>
                {/* Disclaimer/Info Banner - adapts based on order mode */}
                <div 
                  className="p-4 rounded-xl mb-6"
                  style={{ 
                    backgroundColor: orderMode === 'waiter_only' 
                      ? 'rgba(255, 152, 0, 0.08)' 
                      : (tableNumber ? 'rgba(183,140,60,0.08)' : 'var(--sand-100)'),
                    border: orderMode === 'waiter_only'
                      ? '1px solid rgba(255, 152, 0, 0.3)'
                      : (tableNumber ? '1px solid rgba(76, 175, 80, 0.3)' : '1px solid rgba(107, 122, 82, 0.2)')
                  }}
                >
                  <div className="flex items-start gap-3">
                    {orderMode === 'waiter_only' ? (
                      <ClipboardList size={20} className="flex-shrink-0 mt-0.5" style={{ color: '#E65100' }} />
                    ) : tableNumber ? (
                      <Send size={20} className="flex-shrink-0 mt-0.5" style={{ color: '#B78C3C' }} />
                    ) : (
                      <Hand size={20} className="flex-shrink-0 mt-0.5" style={{ color: 'var(--stone-600)' }} />
                    )}
                    <div>
                      <p className="font-medium text-sm mb-1" style={{ 
                        color: orderMode === 'waiter_only' ? '#E65100' : (tableNumber ? '#9E7530' : 'var(--stone-600)')
                      }}>
                        {orderMode === 'waiter_only' 
                          ? t.waiterModeTitle 
                          : (tableNumber ? t.sendOrder : t.showWaiter)}
                      </p>
                      <p className="text-sm" style={{ color: 'var(--stone-500)' }}>
                        {orderMode === 'waiter_only' 
                          ? t.disclaimerWaiterOnly 
                          : (tableNumber ? t.disclaimerTable : t.disclaimer)}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Local Items Section */}
                {getLocalItems().length > 0 && (
                  <div className="mb-6">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="text-lg">🍽️</span>
                      <h3 className="font-serif text-base font-medium" style={{ color: 'var(--stone-800)' }}>
                        {t.localSection}
                      </h3>
                    </div>
                    <div className="space-y-3">
                      {getLocalItems().map((item, index) => (
                        <ItemCard
                          key={`local-${item.id}-${index}`}
                          item={item}
                          index={index}
                          localize={localize}
                          getItemImage={getItemImage}
                          getItemTotal={getItemTotal}
                          handleRemoveItem={handleRemoveItem}
                          handleQtyChange={handleQtyChange}
                        />
                      ))}
                    </div>
                    {getShopItems().length > 0 && (
                      <div className="flex justify-between items-center mt-3 px-2 py-2 rounded-lg" style={{ backgroundColor: 'var(--sand-100)' }}>
                        <span className="text-sm" style={{ color: 'var(--stone-500)' }}>{t.subtotalLocal}</span>
                        <span className="font-semibold text-sm" style={{ color: 'var(--gold)' }}>{getLocalTotal().toFixed(2)} €</span>
                      </div>
                    )}
                  </div>
                )}

                {/* Shop Items Section */}
                {getShopItems().length > 0 && (
                  <div className="mb-6">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="text-lg">🛍️</span>
                      <h3 className="font-serif text-base font-medium" style={{ color: 'var(--stone-800)' }}>
                        {t.shopSection}
                      </h3>
                    </div>
                    <div className="space-y-3">
                      {getShopItems().map((item, index) => (
                        <ItemCard
                          key={`shop-${item.id}-${index}`}
                          item={item}
                          index={index}
                          localize={localize}
                          getItemImage={getItemImage}
                          getItemTotal={getItemTotal}
                          handleRemoveItem={handleRemoveItem}
                          handleQtyChange={handleQtyChange}
                          isShopItem={true}
                        />
                      ))}
                    </div>
                    {getLocalItems().length > 0 && (
                      <div className="flex justify-between items-center mt-3 px-2 py-2 rounded-lg" style={{ backgroundColor: 'var(--sand-100)' }}>
                        <span className="text-sm" style={{ color: 'var(--stone-500)' }}>{t.subtotalShop}</span>
                        <span className="font-semibold text-sm" style={{ color: 'var(--gold)' }}>{getShopTotal().toFixed(2)} €</span>
                      </div>
                    )}
                  </div>
                )}

                {/* Clear button */}
                <button
                  onClick={() => setShowClearConfirm(true)}
                  className="w-full text-center text-sm py-2 mb-6"
                  style={{ color: '#8B3D3F' }}
                  data-testid="clear-selection-btn"
                >
                  {t.clear}
                </button>

                {/* Shop Banner - Non-intrusive invitation (only if no shop items) */}
                {getShopItems().length === 0 && (
                  <div 
                    className="mb-6 p-4 rounded-xl border flex items-center justify-between cursor-pointer transition-all hover:shadow-md"
                    style={{ 
                      backgroundColor: 'rgba(196, 149, 106, 0.08)', 
                      borderColor: 'rgba(196, 149, 106, 0.3)' 
                    }}
                    onClick={() => {
                      setIsOpen(false);
                      window.dispatchEvent(new CustomEvent('navigateToShop'));
                    }}
                    data-testid="shop-banner"
                  >
                    <div className="flex items-center gap-3">
                      <div 
                        className="w-10 h-10 rounded-full flex items-center justify-center"
                        style={{ backgroundColor: 'rgba(196, 149, 106, 0.2)' }}
                      >
                        <ShoppingBag size={20} style={{ color: '#B78C3C' }} />
                      </div>
                      <div>
                        <p className="font-medium text-sm" style={{ color: 'var(--stone-800)' }}>
                          {t.shopBanner}
                        </p>
                        <p className="text-xs" style={{ color: 'var(--stone-500)' }}>
                          {t.shopBannerSubtitle}
                        </p>
                      </div>
                    </div>
                    <ChevronRight size={20} style={{ color: '#B78C3C' }} />
                  </div>
                )}

                {/* Total */}
                <div 
                  className="p-5 rounded-xl mb-6"
                  style={{ backgroundColor: 'var(--stone-800)' }}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-lg" style={{ color: 'rgba(255, 253, 249, 0.8)' }}>
                      {t.total}
                    </span>
                    <span className="font-serif text-3xl font-semibold" style={{ color: '#FBF5E8' }}>
                      {getTotal().toFixed(2)} €
                    </span>
                  </div>
                </div>

                {/* Error message */}
                {orderError && (
                  <div className="p-3 rounded-lg mb-4 bg-red-50 border border-red-200">
                    <p className="text-red-600 text-sm text-center">{orderError}</p>
                  </div>
                )}

                {/* Action buttons - different based on order mode and table */}
                <div className="space-y-3 pb-24">
                  {orderMode === 'waiter_only' ? (
                    // Waiter-only mode: Just show to waiter button
                    <Button
                      onClick={handleShowWaiter}
                      className="w-full py-6 text-base font-medium rounded-full"
                      style={{ backgroundColor: '#E65100', color: 'white' }}
                      data-testid="show-waiter-btn"
                    >
                      <Hand size={20} className="mr-2" />
                      {t.showToWaiter}
                    </Button>
                  ) : tableNumber ? (
                    // Table mode with direct send: Send order + View orders
                    <>
                      <Button
                        onClick={handleSendOrder}
                        disabled={sendingOrder}
                        className="w-full py-6 text-base font-medium rounded-full"
                        style={{ backgroundColor: '#B78C3C', color: 'white' }}
                        data-testid="send-order-btn"
                      >
                        {sendingOrder ? (
                          <>
                            <Loader2 size={20} className="mr-2 animate-spin" />
                            {language === 'es' ? 'Enviando...' : language === 'en' ? 'Sending...' : language === 'fr' ? 'Envoi...' : 'Enviant...'}
                          </>
                        ) : (
                          <>
                            <Send size={20} className="mr-2" />
                            {language === 'es' ? 'Realizar pedido' : language === 'en' ? 'Place order' : language === 'fr' ? 'Passer commande' : 'Fer comanda'}
                          </>
                        )}
                      </Button>
                      <Button
                        onClick={() => setShowOrdersPanel(true)}
                        variant="outline"
                        className="w-full py-4 text-base font-medium rounded-full"
                        data-testid="view-orders-btn"
                      >
                        <ClipboardList size={18} className="mr-2" />
                        {t.viewOrders}
                      </Button>
                    </>
                  ) : (
                    <Button
                        onClick={handleShowWaiter}
                        className="w-full py-6 text-base font-medium rounded-full"
                        style={{ backgroundColor: 'var(--gold)', color: 'white' }}
                        data-testid="show-waiter-btn"
                      >
                        <Hand size={20} className="mr-2" />
                        {t.showWaiter}
                      </Button>
                  )}
                </div>
              </>
            )}
          </div>
        </ScrollArea>
      </div>

      {/* Clear confirmation modal */}
      {showClearConfirm && (
        <div 
          className="fixed inset-0 z-[60] flex items-center justify-center p-4"
          style={{ backgroundColor: 'rgba(26, 24, 22, 0.6)' }}
        >
          <div 
            className="w-full max-w-sm p-6 rounded-2xl animate-scale-in"
            style={{ backgroundColor: 'var(--sand-50)' }}
          >
            <h3 className="font-serif text-xl text-center mb-4" style={{ color: 'var(--stone-800)' }}>
              {language === 'cat' ? 'Buidar selecció?' 
                : language === 'en' ? 'Clear selection?' 
                : language === 'fr' ? 'Vider la sélection ?'
                : '¿Vaciar selección?'}
            </h3>
            <div className="flex gap-3">
              <Button
                onClick={() => setShowClearConfirm(false)}
                variant="outline"
                className="flex-1 py-3 rounded-full"
              >
                {language === 'cat' ? 'Cancel·lar' : language === 'en' ? 'Cancel' : language === 'fr' ? 'Annuler' : 'Cancelar'}
              </Button>
              <Button
                onClick={() => {
                  clearSelection();
                  setShowClearConfirm(false);
                }}
                className="flex-1 py-3 rounded-full"
                style={{ backgroundColor: '#8B3D3F', color: 'white' }}
              >
                {language === 'cat' ? 'Buidar' : language === 'en' ? 'Clear' : language === 'fr' ? 'Vider' : 'Vaciar'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* WAITER MODAL - Ticket style for reading at distance */}
      {showWaiterModal && (
        <WaiterModal
          items={items}
          language={language}
          localize={localize}
          onClose={() => {
            setShowWaiterModal(false);
            const itemCount = items.reduce((sum, item) => sum + item.quantity, 0);
            if (itemCount >= 2) {
              setTimeout(() => {
                if (sorteoActive) {
                  setShowSorteo(true);
                } else {
                  setShowReviewModal(true);
                }
              }, 500);
            }
          }}
          t={t}
        />
      )}

      {/* REVIEW MODAL - Shows after closing waiter modal */}
      <ThankYouReviewModal
        isOpen={showReviewModal}
        onClose={() => setShowReviewModal(false)}
      />

      {/* SORTEO POPUP - Shows instead of review when active */}
      {showSorteo && (
        <SorteoPopup
          onClose={() => {
            setShowSorteo(false);
            // If user skips sorteo, show review modal
            setTimeout(() => setShowReviewModal(true), 300);
          }}
          onParticipated={() => {
            // User participated in sorteo, don't show review (sorteo already invites to review)
          }}
        />
      )}
    </>
  );
};

// Waiter Modal - Premium ticket design for reading at distance
const WaiterModal = ({ items, language, localize, onClose, t }) => {
  return (
    <div 
      className="fixed inset-0 z-[70] flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(26, 24, 22, 0.85)' }}
      onClick={onClose}
      data-testid="waiter-modal"
    >
      <div 
        className="w-full max-w-md rounded-2xl overflow-hidden animate-scale-in flex flex-col"
        style={{ 
          backgroundColor: '#FBF5E8',
          maxHeight: 'calc(100vh - 2rem)'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header - Fixed */}
        <div 
          className="px-6 py-5 text-center shrink-0"
          style={{ backgroundColor: 'var(--stone-800)' }}
        >
          <p className="text-sm tracking-widest uppercase mb-1" style={{ color: 'rgba(255,253,249,0.6)' }}>
            PONT 10
          </p>
          <h2 className="font-serif text-2xl" style={{ color: '#FBF5E8' }}>
            {t.yourSelection}
          </h2>
        </div>

        {/* Items - Scrollable with max height */}
        <div 
          className="px-6 py-6 overflow-y-auto flex-1"
          style={{ maxHeight: 'calc(100vh - 12rem)' }}
        >
          <div className="space-y-4">
            {items.map((item, idx) => {
              // Show Spanish translation under the item if the customer is
              // browsing in a language other than Spanish or Catalan, so the
              // waiter can read the order in their working language.
              const displayName = localize(item.name);
              const esName = (item.name && typeof item.name === 'object') ? item.name.es : item.name;
              const showES = !!esName && language !== 'es' && language !== 'ca' && esName !== displayName;
              return (
                <div 
                  key={`${item.id}-${idx}`}
                  className="flex items-baseline gap-3"
                  style={{ 
                    borderBottom: idx < items.length - 1 ? '1px dashed rgba(45,42,38,0.15)' : 'none', 
                    paddingBottom: idx < items.length - 1 ? '1rem' : 0 
                  }}
                >
                  <span 
                    className="text-3xl font-bold shrink-0"
                    style={{ color: 'var(--gold)', minWidth: '2.5rem' }}
                  >
                    {item.quantity}×
                  </span>
                  <div className="flex-1 min-w-0">
                    <span 
                      className="font-serif text-xl leading-tight break-words block"
                      style={{ color: 'var(--stone-800)' }}
                    >
                      {displayName}
                    </span>
                    {showES && (
                      <span
                        className="font-sans italic block mt-1"
                        style={{ color: '#9E7530', letterSpacing: '0.01em', fontSize: 'clamp(1rem, 2.4vw, 1.35rem)', lineHeight: 1.3 }}
                      >
                        {esName}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Footer with close button - Fixed */}
        <div className="px-6 pb-6 pt-2 shrink-0">
          <button
            onClick={onClose}
            className="w-full py-4 rounded-full font-medium text-lg transition-all"
            style={{ 
              backgroundColor: 'var(--stone-800)',
              color: '#FBF5E8'
            }}
            data-testid="close-waiter-modal"
          >
            {t.close}
          </button>
        </div>
      </div>
    </div>
  );
};

// Suggestion card
const SuggestionCard = ({ product, index }) => {
  const { localize } = useLanguage();
  const { addItem, lastAdded } = useSelection();
  const [added, setAdded] = useState(false);

  useEffect(() => {
    if (lastAdded === product.id) {
      setAdded(true);
      const timer = setTimeout(() => setAdded(false), 1500);
      return () => clearTimeout(timer);
    }
  }, [lastAdded, product.id]);

  const handleAdd = () => {
    addItem(product);
  };

  const getImage = () => {
    // First check image_url (new field for generated images)
    if (product.image_url && product.image_url.startsWith('http')) return product.image_url;
    // Then check images array
    if (product.images?.[0]) return product.images[0];
    return productPlaceholders[index % productPlaceholders.length];
  };

  return (
    <div 
      className="rounded-xl overflow-hidden"
      style={{ 
        backgroundColor: 'var(--sand-100)',
        border: '1px solid rgba(45, 42, 38, 0.06)'
      }}
    >
      <img
        src={getImage()}
        alt={localize(product.name)}
        className="w-full aspect-square object-cover"
      />
      <div className="p-3">
        <h4 
          className="text-sm font-medium line-clamp-2 mb-2"
          style={{ color: 'var(--stone-800)' }}
        >
          {localize(product.name)}
        </h4>
        <div className="flex items-center justify-between">
          <span className="font-semibold" style={{ color: 'var(--gold)' }}>
            {product.price.toFixed(2)} €
          </span>
          <button
            onClick={handleAdd}
            className="w-8 h-8 rounded-full flex items-center justify-center transition-all duration-300"
            style={{ 
              backgroundColor: added ? 'var(--gold)' : 'var(--sand-200)',
              color: added ? 'white' : 'var(--stone-800)'
            }}
          >
            {added ? <Check size={14} /> : <Plus size={14} />}
          </button>
        </div>
      </div>
    </div>
  );
};

// Item card for selection list
const ItemCard = ({ item, index, localize, getItemImage, getItemTotal, handleRemoveItem, handleQtyChange, isShopItem = false }) => {
  return (
    <div
      className="flex gap-3 p-3 rounded-xl"
      style={{ 
        backgroundColor: 'var(--sand-100)',
        border: `1px solid ${isShopItem ? 'rgba(196, 149, 106, 0.2)' : 'rgba(45, 42, 38, 0.06)'}`
      }}
      data-testid={`selection-item-${item.id}`}
    >
      <img
        src={getItemImage(item, index)}
        alt={localize(item.name)}
        className="w-20 h-20 rounded-lg object-cover shrink-0"
      />

      <div className="flex-1 min-w-0">
        <h3 
          className="font-serif text-base font-medium mb-1 line-clamp-2"
          style={{ color: 'var(--stone-800)' }}
        >
          {localize(item.name)}
        </h3>

        {item.selectedOptions?.length > 0 && (
          <div className="mb-1">
            {item.selectedOptions.map((opt, idx) => (
              <p key={idx} className="text-sm" style={{ color: 'var(--stone-500)' }}>
                + {localize(opt.name)}
              </p>
            ))}
          </div>
        )}

        <p className="font-semibold text-lg" style={{ color: isShopItem ? '#B78C3C' : 'var(--gold)' }}>
          {getItemTotal(item).toFixed(2)} €
        </p>
      </div>

      <div className="flex flex-col items-end justify-between shrink-0">
        <button
          onClick={() => handleRemoveItem(item)}
          className="p-1.5 rounded-full transition-colors hover:bg-red-50"
          data-testid={`remove-item-${item.id}`}
        >
          <Trash2 size={16} style={{ color: '#8B3D3F' }} />
        </button>

        <div 
          className="flex items-center gap-1 p-0.5 rounded-full"
          style={{ backgroundColor: 'var(--sand-50)' }}
        >
          <button
            onClick={() => handleQtyChange(item, item.quantity - 1)}
            className="w-8 h-8 rounded-full flex items-center justify-center"
            style={{ backgroundColor: 'var(--sand-200)' }}
            data-testid={`decrease-qty-${item.id}`}
          >
            <Minus size={14} style={{ color: 'var(--stone-800)' }} />
          </button>
          <span 
            className="w-6 text-center text-base font-semibold"
            style={{ color: 'var(--stone-800)' }}
          >
            {item.quantity}
          </span>
          <button
            onClick={() => handleQtyChange(item, item.quantity + 1)}
            className="w-8 h-8 rounded-full flex items-center justify-center"
            style={{ backgroundColor: isShopItem ? '#B78C3C' : 'var(--gold)' }}
            data-testid={`increase-qty-${item.id}`}
          >
            <Plus size={14} style={{ color: 'white' }} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default SelectionSheet;
